import * as THREE from 'three';

export class DamageText {
  private container: HTMLElement;
  private activeTexts: DamageTextElement[] = [];

  constructor() {
    // 创建伤害数字容器
    this.container = document.createElement('div');
    this.container.id = 'damage-text-container';
    this.container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      pointer-events: none;
      z-index: 999;
      overflow: hidden;
    `;
    document.body.appendChild(this.container);
    console.log('DamageText container created');
  }

  showDamage(
    position: THREE.Vector3,
    damage: number,
    camera: THREE.Camera,
    isCritical: boolean = false
  ): void {
    console.log(`Showing damage: ${damage} at position:`, position);
    const element = new DamageTextElement(
      position,
      damage,
      camera,
      isCritical
    );
    this.container.appendChild(element.element);
    this.activeTexts.push(element);
    console.log(`Active damage texts: ${this.activeTexts.length}`);
  }

  update(deltaTime: number, camera: THREE.Camera): void {
    for (let i = this.activeTexts.length - 1; i >= 0; i--) {
      const text = this.activeTexts[i];
      text.update(deltaTime, camera);

      if (!text.isActive) {
        this.container.removeChild(text.element);
        this.activeTexts.splice(i, 1);
      }
    }
  }

  clear(): void {
    this.activeTexts.forEach(text => {
      this.container.removeChild(text.element);
    });
    this.activeTexts = [];
  }
}

class DamageTextElement {
  public element: HTMLElement;
  public isActive: boolean = true;
  private lifetime: number = 1.5;
  private age: number = 0;
  private worldPosition: THREE.Vector3;
  private velocity: THREE.Vector3;
  private screenPosition: THREE.Vector2 = new THREE.Vector2();

  constructor(
    position: THREE.Vector3,
    damage: number,
    camera: THREE.Camera,
    isCritical: boolean
  ) {
    this.worldPosition = position.clone();
    this.worldPosition.y += 2; // 从坦克上方开始

    // 向上飘动
    this.velocity = new THREE.Vector3(
      (Math.random() - 0.5) * 2,
      3 + Math.random() * 2,
      0
    );

    // 创建 DOM 元素
    this.element = document.createElement('div');
    const roundedDamage = Math.round(damage);
    this.element.textContent = isCritical ? `${roundedDamage}!` : `${roundedDamage}`;
    this.element.className = 'damage-number';
    this.element.style.cssText = `
      position: fixed;
      font-size: ${isCritical ? '48px' : '32px'};
      font-weight: bold;
      color: ${isCritical ? '#ff0000' : '#ffff00'};
      text-shadow:
        -2px -2px 0 #000,
        2px -2px 0 #000,
        -2px 2px 0 #000,
        2px 2px 0 #000,
        0 0 10px ${isCritical ? '#ff0000' : '#ffff00'};
      pointer-events: none;
      white-space: nowrap;
      z-index: 10000;
      user-select: none;
    `;

    // 初始位置
    this.updateScreenPosition(camera);
    console.log(`Created damage element at screen position: ${this.screenPosition.x}, ${this.screenPosition.y}`);
  }

  update(deltaTime: number, camera: THREE.Camera): void {
    this.age += deltaTime;

    if (this.age >= this.lifetime) {
      this.isActive = false;
      return;
    }

    // 更新世界位置
    this.worldPosition.add(this.velocity.clone().multiplyScalar(deltaTime));
    this.velocity.y -= 5 * deltaTime; // 重力

    // 更新屏幕位置
    this.updateScreenPosition(camera);

    // 淡出
    const progress = this.age / this.lifetime;
    const opacity = 1 - progress;
    this.element.style.opacity = opacity.toString();
  }

  private updateScreenPosition(camera: THREE.Camera): void {
    // 世界坐标转屏幕坐标
    const vector = this.worldPosition.clone();
    vector.project(camera);

    // 检查是否在相机后面
    if (vector.z > 1) {
      this.element.style.display = 'none';
      return;
    }

    this.element.style.display = 'block';

    // 转换到像素坐标
    this.screenPosition.x = (vector.x * 0.5 + 0.5) * window.innerWidth;
    this.screenPosition.y = (vector.y * -0.5 + 0.5) * window.innerHeight;

    // 使用 transform 而不是 left/top 提高性能
    this.element.style.transform = `translate(${this.screenPosition.x}px, ${this.screenPosition.y}px) translate(-50%, -50%)`;
  }
}

// 添加脉冲动画
const style = document.createElement('style');
style.textContent = `
  @keyframes pulse {
    0% { transform: translate(-50%, -50%) scale(0.5); }
    50% { transform: translate(-50%, -50%) scale(1.2); }
    100% { transform: translate(-50%, -50%) scale(1); }
  }
`;
document.head.appendChild(style);
